import {
  Box,
  AppBar,
  Toolbar,
  Button,
  Snackbar,
  Alert,
  useMediaQuery,
  Link,
} from '@mui/material';
import { useState, useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import Sidebar from './Sidebar';
import LoadingOverlay from '../Common/LoadingOverlay';
import { AppContext } from '../../context/AppContext';
import { api } from '../../services/api';
import { validateProductForm, validateBusinessInfo, hasValidationErrors } from '../../utils/validation';
import netsolLogo from '../../assets/logos/netsol.png';
import ChecklistPopover from './ChecklistPopover';
import { createProductPayload, createBusinessInfoPayload, createPublishPayload } from '../../helpers/payloadHelper';
import { getPostPublishUrl, getCheckoutAccountUrl, getHomeUrl } from '../../helpers/cookieHelper';



const MainLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    state,
    getState,
    snackbar,
    showSnackbar,
    closeSnackbar,
    storeId,
    pid,
    updateProductCreated,
    updateCheckoutPageId,
    sslConnected,
  } = useContext(AppContext);
  const [publishing, setPublishing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePublish = async () => {
    if (!sslConnected) {
      showSnackbar(
        'Please wait while we complete the installation of your Security Certificate. Please check back in 1/2 an hour.',
        'error'
      );
      return;
    }

    // Validate state
    const productErrors = validateProductForm(state.product, state.productType);
    const businessErrors = validateBusinessInfo(state.businessInfo);
    const hasPaymentProviders = state.paymentProviders?.is_stripe_enabled || state.paymentProviders?.is_paypal_enabled

    if (hasValidationErrors(productErrors) || hasValidationErrors(businessErrors) || !hasPaymentProviders) {
      showSnackbar('Please fill in all required fields and connect a payment method before publishing.', 'error');
      return;
    }

    setPublishing(true);
    try {
      const currentState = getState();

      const productPayload = createProductPayload(currentState, pid, storeId);
      const businessInfoPayload = createBusinessInfoPayload(currentState, storeId);

      const createProductResponse = await api.createProduct(productPayload);
      const newProductId = createProductResponse?.data?.productId;
      const createProductErrorMessage = createProductResponse?.data?.errorMessage;
      if (newProductId === null) {
        showSnackbar(
          createProductErrorMessage || 'Failed to create product. Please try again.',
          'error'
        );
        return;
      }
      updateProductCreated(newProductId);
      await api.saveBusinessInfo(businessInfoPayload);

      const productIdForCheckout = newProductId ?? state.productId;
      const existingCheckoutPageId = currentState.checkoutPageId;
      const appInstanceIdForLwCheckout = currentState.appInstanceId ?? storeId;

      let readyToPublish = false;
      if (existingCheckoutPageId) {
        const updateResponse = await api.updateCheckoutPage(
          existingCheckoutPageId,
          appInstanceIdForLwCheckout.toString(),
          productIdForCheckout.toString()
        );
        readyToPublish =
          updateResponse?.success === true && updateResponse?.status === 200;
        if (!readyToPublish) {
          showSnackbar('Failed to publish. Please try again.', 'error');
        }
      } else {
        const response = await createCheckoutPage(currentState, productIdForCheckout);
        if (response?._id != null) {
          updateCheckoutPageId(response._id);
          readyToPublish = true;
        } else {
          showSnackbar('Failed to publish. Please try again.', 'error');
        }
      }

      if (readyToPublish) {
        const publishResponse = await api.publishCheckoutPage();
        if (publishResponse?.success) {
          showSnackbar('Successfully published!', 'success');
          window.location.assign(getPostPublishUrl())
        } else {
          showSnackbar('Failed to publish. Please try again.', 'error');
        }
      }
    } catch (error) {
      showSnackbar(
        error?.errorMessage || error?.message || 'Failed to publish. Please try again.',
        'error'
      );
    } finally {
      setPublishing(false);
    }
  };

  const createCheckoutPage = async (currentState, productIdForPublish) => {
    try {
      const publishPayload = createPublishPayload(
        currentState,
        currentState.appInstanceId ?? storeId,
        productIdForPublish
      );
      const createCheckoutPageResponse = await api.createCheckoutPage(publishPayload);
      return createCheckoutPageResponse.data;
    } catch (error) {
      showSnackbar(error.message || 'Failed to publish. Please try again.', 'error');
      return undefined;
    }
  };

  const handleClose = () => {
    window.location.assign(getCheckoutAccountUrl())
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: '#fff',
          color: '#212121',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Link
              onClick={() => window.location.assign(getHomeUrl())}
              underline="none"
              sx={{ display: 'inline-flex', lineHeight: 0, cursor: 'pointer' }}
            >
              <img src={netsolLogo} alt="NetSol Logo" style={{ height: 25, marginRight: 0 }} />
            </Link>
          </Box>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handlePublish}
              disabled={publishing}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                backgroundColor: '#326EEA',
                color: '#fff',
              }}
              aria-owns={open ? 'mouse-over-popover' : undefined}
              aria-haspopup="true"
              onMouseEnter={handlePopoverOpen}
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
            <ChecklistPopover open={open} anchorEl={anchorEl} handlePopoverClose={handlePopoverClose} />
            <Button
              variant="outlined"
              onClick={handleClose}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderColor: '#326EEA',
                color: '#326EEA',
              }}
            >
              Close
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, overflow: 'hidden' }}>
        <Sidebar />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            overflow: { xs: 'auto', md: 'hidden' },
            scrollbarGutter: { xs: 'stable', md: 'auto' },
            scrollbarWidth: { xs: 'thin', md: 'auto' },
            scrollbarColor: { xs: '#8e8e8e #f1f1f1', md: 'auto' },
            '&::-webkit-scrollbar': {
              width: { xs: 10, md: 0 },
              height: { xs: 10, md: 0 },
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#8e8e8e',
              borderRadius: 8,
              border: '2px solid #f1f1f1',
            },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>

      <LoadingOverlay loading={publishing} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainLayout;
